import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, IconButton, Tooltip, Grid, Card, CardContent,
  InputAdornment, CircularProgress, Alert, Link as MuiLink,
} from '@mui/material';
import {
  Search as SearchIcon, Language as LanguageIcon,
  OpenInNew as OpenInNewIcon, Business as BusinessIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const resolveApiBase = () => {
  const rawBase = process.env.REACT_APP_API_URL;
  if (!rawBase) return '';
  if (typeof window === 'undefined') return rawBase;
  try {
    return new URL(rawBase, window.location.origin).toString().replace(/\/$/, '');
  } catch { return ''; }
};
const API_BASE = resolveApiBase();

// Auth is httpOnly-cookie based (see AuthContext/api.js) — the browser sends
// the session cookie automatically on same-origin requests. Only attach an
// Authorization header if a legacy localStorage token actually exists;
// sending a synthesized "Bearer null" would make the server reject the
// request even though the valid cookie would otherwise authenticate it.
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token && token !== 'null' && token !== 'undefined') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const Subdomains = () => {
  const [search, setSearch] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading, error } = useQuery(
    'subdomains',
    async () => {
      const res = await fetch(`${API_BASE}/api/companies/subdomains`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch subdomain companies');
      return res.json();
    },
    { refetchInterval: 90000 }
  );

  // data is the raw JSON response: { success, companies, summary }
  // Memoize the companies array so downstream memos get a stable reference
  const allCompanies = useMemo(() => data?.companies || [], [data]);

  const companies = useMemo(() => {
    if (!allCompanies.length) return [];
    return allCompanies
      .filter(c => {
        if (search) {
          const q = search.toLowerCase();
          if (!c.name?.toLowerCase().includes(q) &&
              !c.companySlug?.toLowerCase().includes(q) &&
              !`${c.companySlug}.novosolution.org`.includes(q)) return false;
        }
        if (businessTypeFilter !== 'all' && c.businessType !== businessTypeFilter) return false;
        if (tierFilter !== 'all' && c.currentTier !== tierFilter) return false;
        if (statusFilter === 'active' && c.isArchived) return false;
        if (statusFilter === 'inactive' && !c.isArchived) return false;
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allCompanies, search, businessTypeFilter, tierFilter, statusFilter]);

  const businessTypes = useMemo(() => {
    if (!allCompanies.length) return [];
    return [...new Set(allCompanies.map(c => c.businessType).filter(Boolean))].sort();
  }, [allCompanies]);

  const tiers = useMemo(() => {
    if (!allCompanies.length) return [];
    return [...new Set(allCompanies.map(c => c.currentTier).filter(Boolean))].sort();
  }, [allCompanies]);

  // Use summary counts returned by the server (server already knows the full DB state)
  const totalWithSlug = data?.summary?.withSlug ?? allCompanies.length;
  const totalWithoutSlug = data?.summary?.withoutSlug ?? 0;

  const handleCopy = (text) => { navigator.clipboard.writeText(text); };

  if (error) return <Alert severity="error">Failed to load subdomain data: {error.message}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LanguageIcon fontSize="large" /> Subdomain Management
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        View and manage all company subdomains ({`{slug}.novosolution.org`})
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="h4" color="primary">{totalWithSlug}</Typography>
            <Typography variant="body2" color="textSecondary">Active Subdomains</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="h4" color="warning.main">{totalWithoutSlug}</Typography>
            <Typography variant="body2" color="textSecondary">Without Subdomain</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent>
            <Typography variant="h4">{companies.length}</Typography>
            <Typography variant="body2" color="textSecondary">Filtered Results</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" placeholder="Search by name, subdomain, or URL..."
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Business Type</InputLabel>
              <Select value={businessTypeFilter} label="Business Type" onChange={e => setBusinessTypeFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {businessTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier</InputLabel>
              <Select value={tierFilter} label="Tier" onChange={e => setTierFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {tiers.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Subdomain Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <SubdomainTable companies={companies} onCopy={handleCopy} />
      )}
    </Box>
  );
};

const SubdomainTable = ({ companies, onCopy }) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell><strong>Company Name</strong></TableCell>
          <TableCell><strong>Subdomain</strong></TableCell>
          <TableCell><strong>Full URL</strong></TableCell>
          <TableCell><strong>Business Type</strong></TableCell>
          <TableCell><strong>Tier</strong></TableCell>
          <TableCell><strong>Status</strong></TableCell>
          <TableCell><strong>Created</strong></TableCell>
          <TableCell align="center"><strong>Actions</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {companies.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
              <Typography color="textSecondary">No subdomains found matching your filters.</Typography>
            </TableCell>
          </TableRow>
        ) : (
          companies.map(company => {
            const fullUrl = `https://${company.companySlug}.novosolution.org`;
            const tierColors = {
              free: 'default', standard: 'info', professional: 'warning', enterprise: 'success'
            };
            return (
              <TableRow key={company.companyId || company._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium">{company.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={company.companySlug} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell>
                  <MuiLink href={fullUrl} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
                    {fullUrl}
                  </MuiLink>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{company.businessType || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={company.currentTier || 'free'}
                    size="small"
                    color={tierColors[company.currentTier?.toLowerCase()] || 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={company.isArchived ? 'Inactive' : 'Active'}
                    size="small"
                    color={company.isArchived ? 'error' : 'success'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {company.createdAt ? format(new Date(company.createdAt), 'MMM d, yyyy') : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Copy URL">
                    <IconButton size="small" onClick={() => onCopy(fullUrl)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open Website">
                    <IconButton size="small" component="a" href={fullUrl} target="_blank" rel="noopener">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default Subdomains;
